import React, { useState, useRef } from 'react';
import './AddFlashcardGroup.css';
import OnScreenKeyboard from './OnScreenKeyboard';
import { useLanguage } from '../contexts/LanguageContext';
import { getKeyboardLangCode } from '../utils/keyboardUtils';
import { isRTL as isRTLanguage } from '../utils/rtlUtils';
import { isTextSafe } from '../utils/safeForAds';

const AddFlashcardGroup = ({ from, to, onAdd, onCancel }) => {
  const { t, isRTL } = useLanguage();
  const [groupName, setGroupName] = useState('');
  const [pairs, setPairs] = useState([{ left: '', right: '' }]);
  const [contentError, setContentError] = useState('');
  
  // Keyboard state
  const [keyboardArmedLang, setKeyboardArmedLang] = useState(null);
  const [keyboardActiveIdx, setKeyboardActiveIdx] = useState(null);
  const [focusedInputIdx, setFocusedInputIdx] = useState(null);
  const keyboardOverlayRef = useRef(null);
  const rightInputRefs = useRef([]);
  const leftInputRefs = useRef([]);
  


  const handlePairChange = (index, side, value) => {
    const newPairs = [...pairs];
    // Limit toLang words (right side) to 150 characters
    if (side === 'right') {
      newPairs[index][side] = value.substring(0, 150);
    } else {
      newPairs[index][side] = value;
    }
    setPairs(newPairs);
  };

  const handleAddPair = () => {
    setPairs([...pairs, { left: '', right: '' }]);
  };

  const handleRemovePair = (index) => {
    const newPairs = pairs.filter((_, i) => i !== index);
    setPairs(newPairs);
    // Reset keyboard state if the active input was removed
    if (keyboardActiveIdx === index) {
      setKeyboardActiveIdx(null);
      setKeyboardArmedLang(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setContentError(''); // Clear previous errors
    
    // Check group name
    if (!isTextSafe(groupName)) {
      setContentError(t('inappropriateContentDetected'));
      return;
    }
    
    // Check all word pairs
    for (let i = 0; i < pairs.length; i++) {
      if (!isTextSafe(pairs[i].left)) {
        setContentError(t('inappropriateContentDetected'));
        return;
      }
      if (!isTextSafe(pairs[i].right)) {
        setContentError(t('inappropriateContentDetected'));
        return;
      }
    }
    
    // Original validation
    if (!groupName.trim() || pairs.some(p => !p.left.trim() || !p.right.trim())) {
      setContentError(t('pleaseProvideGroupNameAndWords'));
      return;
    }

    // Explicit sides prevent the term from being overwritten by the definition
    // when both sides of a definition module use the same language.
    const wordPairs = pairs.map(p => ({ left: p.left, right: p.right }));
    onAdd(groupName, wordPairs);
  };

  return (
    <div className="add-flashcard-group-container">
      <form onSubmit={handleSubmit} className="add-flashcard-group-form">
        <h3>{t('createANewWordGroup')}</h3>
        <p className="form-description">{t('giveGroupNameAndAddWords')}</p>
        
        {/* Add error display */}
        {contentError && (
          <div className="error-message" style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
            {contentError}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="groupName">{t('groupName')}</label>
          <input
            id="groupName"
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value.substring(0, 100))}
            placeholder={t('groupNamePlaceholder')}
            maxLength={100}
            required
          />
          {groupName.length >= 100 && (
            <div style={{ 
              color: '#d32f2f', 
              fontSize: '0.8rem', 
              marginTop: '4px' 
            }}>
              {t('groupNameMaxLength') || 'Group name cannot exceed 100 characters'}
            </div>
          )}
        </div>

        <div className="word-pairs-section">
          <div className="language-direction-header">
            <span>{from === to ? t('term') : t(from)}</span>
            <span>{from === to ? t('definition') : t(to)}</span>
          </div>
          {pairs.map((pair, index) => {
            const isSourceRTL = isRTLanguage(getKeyboardLangCode(from));
            const isTargetRTL = isRTLanguage(getKeyboardLangCode(to));
            return (
              <div key={index} className="word-pair-row">
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder={from === to ? t('term') : `${t(from)} word`}
                    value={pair.left}
                    onChange={(e) => handlePairChange(index, 'left', e.target.value)}
                    style={{ 
                      direction: isSourceRTL ? 'rtl' : 'ltr',
                      textAlign: isSourceRTL ? 'right' : 'left'
                    }}
                    ref={el => leftInputRefs.current[index] = el}
                    required
                  />
                </div>
                <span className="arrow-separator">{isRTL ? '←' : '→'}</span>
                <div className="input-with-keyboard-icon" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder={from === to ? t('definition') : `${t(to)} translation`}
                    value={pair.right}
                    onChange={(e) => handlePairChange(index, 'right', e.target.value)}
                    maxLength={150}
                    style={{ 
                      direction: isTargetRTL ? 'rtl' : 'ltr',
                      textAlign: isTargetRTL ? 'right' : 'left',
                      paddingRight: !isTargetRTL ? '2.2rem' : undefined,
                      paddingLeft: isTargetRTL ? '2.2rem' : undefined
                    }}
                    ref={el => rightInputRefs.current[index] = el}
                    onFocus={() => {
                      setFocusedInputIdx(`right-${index}`);
                      if (keyboardArmedLang === getKeyboardLangCode(to)) {
                        setKeyboardActiveIdx(index);
                      }
                    }}
                    onBlur={e => {
                      setFocusedInputIdx(null);
                      setTimeout(() => {
                        const active = document.activeElement;
                        const isKeyboardKey = active && (
                          active.classList.contains('keyboard-key') ||
                          active.closest('.on-screen-keyboard-overlay')
                        );
                        const isSameLang = rightInputRefs.current.some((input, i) =>
                          input === active && getKeyboardLangCode(to) === keyboardArmedLang
                        );
                        if (!isKeyboardKey && !isSameLang) {
                          setKeyboardActiveIdx(null);
                        }
                      }, 100);
                    }}
                    required
                  />
                  {pair.right.length >= 150 && (
                    <div style={{ 
                      color: '#d32f2f', 
                      fontSize: '0.8rem', 
                      marginTop: '4px',
                      textAlign: isTargetRTL ? 'right' : 'left'
                    }}>
                      {t('toLangMaxLength') || 'Translation cannot exceed 150 characters'}
                    </div>
                  )}
                  {focusedInputIdx === `right-${index}` && (
                    <div className="keyboard-icon-absolute" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: isTargetRTL ? 'auto' : '0.3rem', left: isTargetRTL ? '0.3rem' : 'auto', zIndex: 2 }}>
                      <button
                        type="button"
                        className="btn-toggle-keyboard subtle-toggle"
                        tabIndex={-1}
                        style={{
                          marginBottom: '0.25rem',
                          fontSize: '1.1rem',
                          background: 'none',
                          border: 'none',
                          color: '#888',
                          padding: '0.15rem 0.5rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '1.8rem',
                          width: '1.8rem',
                        }}
                        aria-label={t('showKeyboard')}
                        onMouseDown={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          const langCode = getKeyboardLangCode(to);
                          if (keyboardArmedLang === langCode) {
                            setKeyboardArmedLang(null);
                            setKeyboardActiveIdx(null);
                          } else {
                            setKeyboardArmedLang(langCode);
                            setKeyboardActiveIdx(index);
                            rightInputRefs.current[index]?.focus();
                          }
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="5" width="18" height="14" rx="2"/>
                          <line x1="7" y1="15" x2="7" y2="15"/>
                          <line x1="11" y1="15" x2="11" y2="15"/>
                          <line x1="15" y1="15" x2="15" y2="15"/>
                          <line x1="7" y1="11" x2="7" y2="11"/>
                          <line x1="11" y1="11" x2="11" y2="11"/>
                          <line x1="15" y1="11" x2="15" y2="11"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                {pairs.length > 1 && (
                  <button type="button" onClick={() => handleRemovePair(index)} className="btn-remove-pair" aria-label={t('removeWordPair')}>
                    &times;
                  </button>
                )}
              </div>
            );
          })}
          <button type="button" onClick={handleAddPair} className="btn-add-pair" aria-label={t('addAnotherWord')}>
            {t('addAnotherWord')}
          </button>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn btn-secondary">{t('cancel')}</button>
          <button type="submit" className="btn btn-primary">{t('createWordGroup')}</button>
        </div>
      </form>
      
      {keyboardArmedLang && keyboardActiveIdx !== null && (
        <div ref={keyboardOverlayRef}>
          <OnScreenKeyboard
            languageCode={keyboardArmedLang}
            onInsert={(char) => {
              // Determine which input is active (left or right)
              const leftInput = leftInputRefs.current[keyboardActiveIdx];
              const rightInput = rightInputRefs.current[keyboardActiveIdx];
              const activeInput = leftInput === document.activeElement ? leftInput : rightInput;
              
              if (activeInput) {
                const start = activeInput.selectionStart;
                const end = activeInput.selectionEnd;
                const currentValue = activeInput.value;
                
                let newValue;
                if (char === 'backspace') {
                  if (start === end) {
                    newValue = currentValue.slice(0, start - 1) + currentValue.slice(end);
                    activeInput.setSelectionRange(start - 1, start - 1);
                  } else {
                    newValue = currentValue.slice(0, start) + currentValue.slice(end);
                    activeInput.setSelectionRange(start, start);
                  }
                } else if (char === 'space') {
                  newValue = currentValue.slice(0, start) + ' ' + currentValue.slice(end);
                  activeInput.setSelectionRange(start + 1, start + 1);
                } else {
                  newValue = currentValue.slice(0, start) + char + currentValue.slice(end);
                  activeInput.setSelectionRange(start + char.length, start + char.length);
                }
                
                // Update React state based on which input was active
                if (activeInput === leftInput) {
                  handlePairChange(keyboardActiveIdx, 'left', newValue);
                } else {
                  handlePairChange(keyboardActiveIdx, 'right', newValue);
                }
                
                // Keep focus on the input
                activeInput.focus();
              }
            }}
            onClose={() => {
              setKeyboardArmedLang(null);
              setKeyboardActiveIdx(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AddFlashcardGroup;
